/**
 * Google Analytics (GA4).
 *
 * The measurement id rides in `/config.json` rather than the bundle, so one
 * built artifact serves every stage and a stage that leaves it unset — local
 * dev and staging — sends nothing at all.
 *
 * Three deliberate narrowings, all from one rule: what reaches Google should be
 * the shape of a page, never who was on it.
 *   - Paths are reported as route patterns (`/t/:tid/d/:did/schedule`), so no
 *     tournament or division id leaves the browser and the Pages report
 *     aggregates by page type instead of splintering one row per event. The
 *     query string is dropped for the same reason — `?tournament=` carries an
 *     id.
 *   - `/admin` *pageviews* are not reported. Staff running an event are not
 *     audience, and on a site this size their console browsing would swamp the
 *     real numbers. What they *do* there is reported — building a bracket,
 *     scoring a game — because that is the operational funnel rather than
 *     browsing, and it reports against console route patterns like any other
 *     hit.
 *   - Event parameters are filtered against `ALLOWED_PARAMS`, which admits
 *     enumerated choices, short facet labels, counts, and flags — and nothing
 *     else. A coach's search text and every id fail that test.
 *
 * All three live in `record`, the single door both pageviews and events go
 * through, rather than at the call sites. That placement is the point: gtag.js
 * attaches the live `document.location` to *every* hit it sends, so an event
 * fired on a tournament page ships the id in `page_location` unless something
 * overrides it. Leaving that to per-call-site discipline would mean the rules
 * hold until the next event someone adds.
 */
import { useEffect, useRef } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { loadConfig } from "../config";

const ADMIN_PREFIX = "/admin";

/**
 * Every non-admin route App.tsx renders, as patterns. Flat because these are
 * matched whole: `matchPath` requires a full match, so `/t/:tid` cannot swallow
 * `/t/:tid/d/:did/schedule` and the order here carries no meaning.
 */
const REPORTED_ROUTES = [
  "/",
  "/tournaments",
  "/about",
  "/host",
  "/host/new",
  "/login",
  "/signup",
  "/verify",
  "/forgot-password",
  "/me",
  "/me/profile",
  "/me/teams",
  "/me/register",
  "/t/:tid",
  "/t/:tid/d/:did/standings",
  "/t/:tid/d/:did/schedule",
  "/t/:tid/d/:did/bracket",
] as const;

/**
 * The admin console's routes, as patterns. A table of their own because the
 * two are treated differently one step downstream: `record` drops a pageview on
 * any of these and keeps an event. They need patterns for the same reason the
 * public ones do — `/admin/t/:tid` carries a tournament id exactly like `/t/:tid`.
 */
const ADMIN_ROUTES = [
  "/admin",
  "/admin/t/:tid",
  "/admin/t/:tid/setup",
  "/admin/t/:tid/teams",
  "/admin/t/:tid/schedule",
  "/admin/t/:tid/scores",
  "/admin/t/:tid/playoffs",
  "/admin/venues",
  "/admin/admins",
  "/admin/users",
  "/admin/branding",
  "/admin/about",
] as const;

/** What an unrecognized URL is reported as. See `analyticsPath`. */
export const UNKNOWN_ROUTE = "/*";

/** Whether a pathname is inside the admin console. */
export function isAdminPath(pathname: string): boolean {
  return pathname === ADMIN_PREFIX || pathname.startsWith(`${ADMIN_PREFIX}/`);
}

/**
 * The route pattern to report for a pathname. Every URL resolves to one, admin
 * included; *whether* a hit is sent is `record`'s call. Keeping the two
 * separate is what stops an id reaching Google by way of a page this module
 * decided not to report — a path with no pattern would otherwise have to fall
 * back to something, and the only thing available is the real URL.
 */
export function analyticsPath(pathname: string): string {
  for (const pattern of [...REPORTED_ROUTES, ...ADMIN_ROUTES]) {
    if (matchPath(pattern, pathname)) return pattern;
  }
  // App.tsx ends in a catch-all that renders the home page, so an unknown URL
  // is still a real pageview. Reporting the literal path would leak whatever
  // the visitor typed, and a route added later without a line above lands here
  // too — one bucket says "off the map" without doing either.
  return UNKNOWN_ROUTE;
}

/* ------------------------------------------------------------------ params */

export type ParamValue = string | number | boolean;
export type EventParams = Record<string, ParamValue>;

/**
 * Long enough for a division name an organizer typed ("18U Gold Showcase"),
 * short enough that nothing resembling a payload fits.
 */
const MAX_LABEL = 32;

type Rule =
  | { kind: "enum"; values: readonly string[] }
  /** A facet value the visitor picked from a dropdown, never typed. */
  | { kind: "label" }
  | { kind: "count" }
  | { kind: "flag" };

/**
 * Every parameter an event may carry, and the shape a legal value takes. A key
 * missing from this table is dropped, and so is a value that does not fit — so
 * the failure mode of adding an event carelessly is a missing dimension in a
 * report, not a leak.
 *
 * `state` and `division` are `label` rather than `enum` because their values
 * come from the tournament data, which organizers author and this file cannot
 * enumerate. They are still safe: both are chosen from a facet dropdown built
 * out of published events, so they describe the catalog, not the visitor.
 */
const ALLOWED_PARAMS: Record<string, Rule> = {
  method: { kind: "enum", values: ["email"] },
  from: { kind: "enum", values: ["tournament_link", "dropdown"] },
  timeframe: { kind: "enum", values: ["upcoming", "past", "all"] },
  registration: { kind: "enum", values: ["all", "open"] },
  sort: { kind: "enum", values: ["date", "name"] },
  state: { kind: "label" },
  division: { kind: "label" },
  result_count: { kind: "count" },
  has_query: { kind: "flag" },
  rebuild: { kind: "flag" },
};

function fits(rule: Rule, value: ParamValue): boolean {
  switch (rule.kind) {
    case "enum":
      return typeof value === "string" && rule.values.includes(value);
    case "label":
      return typeof value === "string" && value.length > 0 && value.length <= MAX_LABEL;
    case "count":
      return typeof value === "number" && Number.isInteger(value) && value >= 0;
    case "flag":
      return typeof value === "boolean";
  }
}

/** The parameters of `params` that `ALLOWED_PARAMS` admits, and only those. */
export function safeParams(params: EventParams): EventParams {
  const out: EventParams = {};
  for (const [key, value] of Object.entries(params)) {
    const rule = ALLOWED_PARAMS[key];
    if (rule && fits(rule, value)) out[key] = value;
  }
  return out;
}

/* --------------------------------------------------------------- transport */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

interface Hit {
  name: string;
  /**
   * The route pattern the hit happened on, resolved when it was recorded
   * rather than when it is sent. A hit queued before the config lands can
   * outlive the navigation that produced it, and reporting it against
   * whatever page the visitor reached in the meantime would be wrong.
   */
  path: string;
  params: EventParams;
}

/**
 * Hits that happened before `/config.json` resolved. The fetch is fast and
 * same-origin, so this holds the first navigation or two at most — but dropping
 * them would lose every visitor's landing page, which is the one pageview that
 * matters most, and the CTA click of anyone who moves faster than the fetch.
 */
let pending: Hit[] = [];
/** Set once the measurement id is known: sends, or drops when GA is off. */
let deliver: ((hit: Hit) => void) | undefined;
let startedLoading = false;

function installGtag(measurementId: string): (hit: Hit) => void {
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  const dataLayer = (window.dataLayer ??= []);
  // The upstream snippet's shape, kept verbatim: gtag.js reads the raw
  // `arguments` object back off dataLayer, and an array is not equivalent.
  function gtag(...__args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  // Pageviews are sent by hand below, because gtag's own would report the real
  // URL, ids and all.
  //
  // This flag settles the automatic pageview on the hard load. It does *not*
  // settle Enhanced Measurement's "Page changes based on browser history
  // events", which is a property setting, is on by default, and fires its own
  // `page_view` — carrying `document.location` — on every SPA navigation. That
  // switch has to be turned off in the GA4 admin UI; nothing here can do it.
  // See the analytics section of the README.
  gtag("config", measurementId, { send_page_view: false });

  return (hit) => {
    gtag("event", hit.name, {
      ...hit.params,
      // On every hit, not just the pageview: this is the override that keeps
      // the live URL — ids, query string and all — out of the report.
      page_location: `${window.location.origin}${hit.path}`,
    });
  };
}

function start(): void {
  if (startedLoading) return;
  startedLoading = true;
  void loadConfig()
    .then(({ gaMeasurementId }) => {
      deliver = gaMeasurementId ? installGtag(gaMeasurementId) : () => {};
      for (const hit of pending) deliver(hit);
    })
    // A missing or broken config.json already fails loudly everywhere that
    // matters; analytics is not the place to raise it a second time.
    .catch(() => {
      deliver = () => {};
    })
    .finally(() => {
      pending = [];
    });
}

/** The one hit the admin console suppresses. See `record`. */
const PAGE_VIEW = "page_view";

/**
 * The one door. Resolves the page to a pattern (dropping admin pageviews),
 * keeps only the parameters the table admits, and sends or queues the result.
 *
 * The pathname is a parameter so a pageview can report the location the router
 * just moved to: an event fired from a click reads it off `window`, but the
 * pageview effect is the one caller that knows the new location first-hand.
 */
function record(name: string, params: EventParams, pathname = window.location.pathname): void {
  start();
  // The admin rule, narrowed to what it was always about: staff browsing would
  // swamp the Pages report of a site this size, so their pageviews stay out —
  // but what they do in the console is the operational funnel this module is
  // asked to measure, and those events go through. Both halves still report
  // against a pattern, and both still pass through `safeParams`.
  if (name === PAGE_VIEW && isAdminPath(pathname)) return;
  const hit: Hit = { name, path: analyticsPath(pathname), params: safeParams(params) };
  if (deliver) deliver(hit);
  else pending.push(hit);
}

/* ------------------------------------------------------------------ events */

/**
 * Top of the registration funnel: the "Register a team" button on a
 * tournament page. Paired with `registration_submitted` it gives the drop-off
 * between wanting to register and managing to.
 */
export function trackRegisterStart(): void {
  record("register_start", {});
}

/**
 * The conversion. `from` separates a coach who arrived on the prefilled link
 * from a tournament page ("tournament_link") from one who picked the event out
 * of the dropdown ("dropdown") — which is what says whether the CTA is doing
 * any work.
 */
export function trackRegistrationSubmitted(from: "tournament_link" | "dropdown"): void {
  record("registration_submitted", { from });
}

/**
 * A team was created. The mid-funnel stall worth watching: a coach with no team
 * cannot register one, and the register form can only point them away.
 */
export function trackTeamCreated(): void {
  record("team_created", {});
}

/**
 * A GA4 recommended event, fired where the account actually starts existing —
 * the verification step. Firing it at the signup form instead would count
 * every address that never came back with a code.
 */
export function trackSignUp(): void {
  record("sign_up", { method: "email" });
}

/** A GA4 recommended event. Sign-in is email and password only, hence `method`. */
export function trackLogin(): void {
  record("login", { method: "email" });
}

/**
 * The tournament hub was narrowed, and to what.
 *
 * Deliberately not GA4's recommended `search` event, which carries the raw
 * `search_term`: the search box fires on every keystroke, and the text is the
 * visitor's own words — a bigger leak than the query string this module
 * already drops on purpose. `has_query` records that someone searched; the
 * bounded facets record what they were looking for. Callers debounce.
 */
export function trackFilterApplied(applied: {
  state: string;
  division: string;
  registration: string;
  timeframe: string;
  sort: string;
  hasQuery: boolean;
  resultCount: number;
}): void {
  record("filter_applied", {
    state: applied.state,
    division: applied.division,
    registration: applied.registration,
    timeframe: applied.timeframe,
    sort: applied.sort,
    has_query: applied.hasQuery,
    result_count: applied.resultCount,
  });
}

/* ---------------------------------------------------------- admin console */

/**
 * A pool game was added. The button reads "Create schedule" because filling a
 * pool is the job an organizer is doing, but the call adds one game — so a
 * count of these is games built, not schedules built.
 */
export function trackGameCreated(): void {
  record("game_created", {});
}

/**
 * A game's result was written. This is the whole of scoring: the Final button
 * sends both scores and the final status in one call, so there is no
 * score-but-not-final step for a second event to mark. Cancelling a game
 * travels with Final in the UI and writes a result too — deliberately not this
 * event, because a called-off game is not a played one.
 */
export function trackGameScored(): void {
  record("game_scored", {});
}

/**
 * Seeds were locked for a division — the gate bracket creation sits behind.
 * Fires on a re-finalize too: redoing it is a real step in running an event,
 * and the gap between this and `bracket_created` is what shows seeding
 * stalling.
 */
export function trackSeedingFinalized(): void {
  record("seeding_finalized", {});
}

/**
 * A bracket was built. `rebuild` separates the first build from a rebuild over
 * an existing one — the same button, and the one that clears scores, so the two
 * are worth telling apart.
 */
export function trackBracketCreated(rebuild: boolean): void {
  record("bracket_created", { rebuild });
}

/* ------------------------------------------------------------- page views */

/**
 * The public pages that also get a named event of their own, by pattern.
 *
 * These duplicate what the Pages report already shows for the same three
 * patterns. They earn it by being events: `bracket_viewed` can head a funnel or
 * an exploration without anyone recalling which path spells a bracket, and it
 * keeps reporting the same way if those URLs are ever restyled.
 *
 * They ride on the pageview effect rather than on the three route components,
 * because the StrictMode guard below is what keeps a dev double-mount from
 * counting twice — three more copies of it would be three more things to keep
 * right.
 */
const VIEW_EVENTS: Record<string, string> = {
  "/t/:tid": "tournament_viewed",
  "/t/:tid/d/:did/schedule": "schedule_viewed",
  "/t/:tid/d/:did/bracket": "bracket_viewed",
};

/**
 * Everything one navigation reports: the pageview, plus the named view event
 * if the page has one. Split out of the hook below so the rules it lands on —
 * that an admin pageview is dropped, that a bracket page also reports
 * `bracket_viewed` — can be tested without a DOM to run an effect in.
 *
 * `useAnalytics` is the caller; this is exported for those tests. Calling it
 * directly from a component would skip the StrictMode guard below and count
 * every dev double-mount twice.
 */
export function recordPageView(pathname: string): void {
  record(PAGE_VIEW, {}, pathname);
  const view = VIEW_EVENTS[analyticsPath(pathname)];
  if (view) record(view, {}, pathname);
}

/**
 * Sends a GA4 pageview per client-side navigation. Call once, above the routes
 * — the auth pages are their own shell, so hanging this off `Layout` would miss
 * every sign-in page.
 */
export function useAnalytics(): void {
  const location = useLocation();
  // StrictMode mounts effects twice in dev, replaying the effect for one
  // navigation. Both halves of this guard earn their place: `key` is per
  // history entry, so two tournaments in a row still count twice even though
  // they share a pattern; `pathname` covers the case `key` cannot see, a
  // history entry pushed without a key of its own — every such entry reads
  // back as "default", and keying on that alone would silently drop the
  // second of two consecutive ones.
  const lastSent = useRef<string>();

  useEffect(() => {
    const seen = `${location.key} ${location.pathname}`;
    if (lastSent.current === seen) return;
    lastSent.current = seen;
    recordPageView(location.pathname);
  }, [location.key, location.pathname]);
}
